import React, { useState, useEffect } from 'react';
import { FaUpload, FaTimes, FaTrash, FaSearch, FaFileAlt } from 'react-icons/fa';
import { useStore } from '../contexts/StoreContext';
import {
  getAllModifiers,
  importModifiers,
  deleteModifier,
  clearStoreModifiers
} from '../services/modifiersService';

// ── DATE FORMAT (sin problema de timezone) ────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(String(dateStr).replace('Z', '').replace('T', ' '));
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: '2-digit' })
    + ' ' + d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const formatCurrency = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? '—' : `$${n.toFixed(2)}`;
};

function Modifiers() {
  const { selectedStore } = useStore();

  const [modifiers, setModifiers]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  // Búsqueda / filtros
  const [search, setSearch]         = useState('');
  const [filterVoid, setFilterVoid] = useState('all'); // 'all' | 'voided' | 'active'

  // Import modal
  const [showImport, setShowImport]   = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting]     = useState(false);

  // Delete modal
  const [deleteId, setDeleteId]       = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);

  useEffect(() => {
    if (selectedStore) fetchModifiers();
  }, [selectedStore]);

  const fetchModifiers = async () => {
    try {
      setLoading(true); setError(null);
      const res = await getAllModifiers(selectedStore.id);
      if (res.success) setModifiers(res.data);
    } catch {
      setError('Error loading modifiers');
    } finally {
      setLoading(false);
    }
  };

  // ── IMPORT ────────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      alert('Please select a valid CSV file'); e.target.value = ''; return;
    }
    setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    try {
      setImporting(true);
      const result = await importModifiers(selectedFile, selectedStore.id);
      if (result.success) {
        alert(
          `Import completed!\n\n` +
          `Total rows: ${result.stats.total}\n` +
          `Imported:   ${result.stats.imported}\n` +
          `Skipped:    ${result.stats.skipped}` +
          (result.stats.errors ? `\n\nErrors (${result.stats.errors.length}):\n` + result.stats.errors.slice(0, 5).join('\n') : '')
        );
        await fetchModifiers();
        handleCloseImport();
      }
    } catch (err) {
      alert('Error importing: ' + (err.response?.data?.message || err.message));
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImport = () => { setShowImport(false); setSelectedFile(null); };

  // ── DELETE ────────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await deleteModifier(deleteId);
      await fetchModifiers();
      setDeleteId(null);
    } catch { alert('Error deleting record'); }
    finally { setLoading(false); }
  };

  const handleClearAll = async () => {
    try {
      setLoading(true);
      await clearStoreModifiers(selectedStore.id);
      await fetchModifiers();
      setShowClearModal(false);
    } catch { alert('Error clearing modifiers'); }
    finally { setLoading(false); }
  };

  // ── FILTROS ───────────────────────────────────────────────────────────
  const filtered = modifiers.filter(m => {
    const term = search.toLowerCase();
    const matchSearch = !term ||
      (m.modifier      && m.modifier.toLowerCase().includes(term))      ||
      (m.server        && m.server.toLowerCase().includes(term))         ||
      (m.parentItemName && m.parentItemName.toLowerCase().includes(term))||
      (m.optionGroupName && m.optionGroupName.toLowerCase().includes(term));
    const matchVoid =
      filterVoid === 'all'    ? true :
      filterVoid === 'voided' ? m.isVoid === 1 :
                                m.isVoid !== 1;
    return matchSearch && matchVoid;
  });

  // ── STATS ─────────────────────────────────────────────────────────────
  const totalGross = filtered.reduce((s, m) => s + (parseFloat(m.grossPrice) || 0), 0);
  const totalNet   = filtered.reduce((s, m) => s + (parseFloat(m.netPrice)   || 0), 0);
  const voidCount  = filtered.filter(m => m.isVoid === 1).length;

  if (!selectedStore) {
    return (
      <div className="pos-data-page">
        <div className="page-header"><h1>Modifiers</h1></div>
        <div className="no-store-message"><p>Please select a store to view modifiers</p></div>
      </div>
    );
  }

  return (
    <div className="pos-data-page">

      {/* HEADER */}
      <div className="page-header sticky-header">
        <div className="header-left">
          <h1>Modifiers</h1>
          <span className="store-indicator">{selectedStore.name}</span>
        </div>
        <div className="header-buttons">
          {modifiers.length > 0 && (
            <button className="btn-danger-outline" onClick={() => setShowClearModal(true)} disabled={loading}>
              <FaTrash /> Clear All
            </button>
          )}
          <button className="btn-primary" onClick={() => setShowImport(true)} disabled={loading}>
            <FaUpload /> Import CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchModifiers}>Retry</button>
        </div>
      )}

      {/* STATS */}
      <div className="stats-bar">
        <div className="stat-item"><span className="stat-label">Total records:</span><strong>{filtered.length}</strong></div>
        <div className="stat-item"><span className="stat-label">Gross:</span><strong>{formatCurrency(totalGross)}</strong></div>
        <div className="stat-item"><span className="stat-label">Net:</span><strong>{formatCurrency(totalNet)}</strong></div>
        <div className="stat-item"><span className="stat-label">Voided:</span><strong>{voidCount}</strong></div>
      </div>

      {/* MODAL IMPORT */}
      {showImport && (
        <div className="modal-overlay" onClick={handleCloseImport}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import Modifiers CSV</h2>
              <button className="btn-close" onClick={handleCloseImport}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <p className="store-indicator">Importing to: <strong>{selectedStore?.name}</strong></p>
              </div>
              <div className="form-group">
                <label>Select CSV File *</label>
                <input type="file" accept=".csv" onChange={handleFileSelect} disabled={importing} className="file-input" />
                {selectedFile && (
                  <p style={{ marginTop: 8, fontSize: 13, color: '#64748b' }}>Selected: {selectedFile.name}</p>
                )}
              </div>
              <div className="inv-form-hint" style={{ marginTop: 12 }}>
                <strong>Expected columns:</strong>
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  Location, Order Id, Order #, Sent Date, Order Date, Check Id, Server, Table, Dining Area, Service, Dining Option, Item Selection Id, Modifier Id, Master Id, Modifier SKU, Modifier PLU, Modifier, Option Group ID, Option Group Name, Parent Menu Selection Item ID, Parent Menu Selection, Sales Category, Gross Price, Discount, Net Price, Qty, Void?, Void Reason ID, Void Reason
                </p>
                <p style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>
                  ✅ Empty fields and values of 0 are handled correctly.
                </p>
              </div>
              {importing && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                  <div className="loading-spinner" />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseImport} disabled={importing}>Cancel</button>
              <button className="btn-primary" onClick={handleImport} disabled={importing || !selectedFile}>
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DELETE SINGLE */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete modifier</h2>
              <button className="btn-close" onClick={() => setDeleteId(null)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this record?</p>
              <p className="inv-warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDeleteId(null)} disabled={loading}>Cancel</button>
              <button className="btn-danger"    onClick={handleConfirmDelete}      disabled={loading}>
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CLEAR ALL */}
      {showClearModal && (
        <div className="modal-overlay" onClick={() => setShowClearModal(false)}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Clear all modifiers</h2>
              <button className="btn-close" onClick={() => setShowClearModal(false)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <p>This will delete <strong>all {modifiers.length} modifier records</strong> for <strong>{selectedStore.name}</strong>.</p>
              <p className="inv-warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowClearModal(false)} disabled={loading}>Cancel</button>
              <button className="btn-danger"    onClick={handleClearAll}                 disabled={loading}>
                {loading ? 'Deleting...' : 'Clear All'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Modifiers;